import { extractPatientNameFromContract } from "#/lib/contractFormValues";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { MedicalRecordRequest } from "../../api/medical-record-requests.type";
import { SubmitButton } from "../../components/shared/buttons/submit-button";
import Typography from "../../components/shared/typography";
import { formatPhoneNumberToHumanReadable } from "../../components/utls/utils";
import { useMedicalRecordRequestsContext } from "../context/MedicalRecordRequestsContext";

function formatLabel(value: string) {
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatDate(input: string) {
  const date = new Date(input);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
}

function formatYesNo(value?: boolean | null) {
  if (value === undefined || value === null) {
    return "Unknown";
  }
  return value ? "Yes" : "No";
}

interface MedicalRecordRequestListItemProps {
  item: MedicalRecordRequest;
  detailPath?: string;
}

export function MedicalRecordRequestListItem({
  item,
  detailPath,
}: MedicalRecordRequestListItemProps) {
  const { markReadyToSend, isMarking, markingRequestId } =
    useMedicalRecordRequestsContext();
  const navigate = useNavigate();

  const isPendingForItem = isMarking && markingRequestId === item.id;
  const disableWhileOtherMutates = isMarking && markingRequestId !== item.id;
  const isReady = item.status === "ready";
  const isDisabled = isReady || disableWhileOtherMutates;

  const recipientName = item.recipientName ?? item.recipient_name ?? null;
  const deliveryMethod = item.deliveryMethod ?? item.delivery_method ?? null;
  const faxNumber = item.faxNumber ?? item.fax_number ?? null;
  const patientName =
    item.patientName ??
    item.patient_name ??
    extractPatientNameFromContract(item as unknown as Record<string, any>);
  const patientLabel = patientName || item.patientId || "Unknown patient";
  const institutionLabel = recipientName || "Unknown institution";
  const signatureImageValue = item.hasSignatureImage ?? null;

  const handleMarkReady = useCallback(async () => {
    if (isDisabled) return;
    await markReadyToSend(item.id);
  }, [isDisabled, item.id, markReadyToSend]);

  return (
    <li className="py-4">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="space-y-1 flex-1">
          <Typography variant="h4" className="text-terracotta">
            {patientLabel} → {institutionLabel}
          </Typography>
          <Typography variant="body1" className="text-terracotta">
            Status: {formatLabel(item.status)}
          </Typography>
          <Typography variant="body2" className="text-terracotta/80">
            Request ID: {item.id}
          </Typography>
          <Typography variant="body2" className="text-terracotta/80">
            Patient ID: {item.patientId}
          </Typography>
          <Typography variant="body2" className="text-terracotta/60">
            Created: {formatDate(item.createdAt)}
          </Typography>
          {recipientName && (
            <Typography variant="body2" className="text-terracotta/80">
              Institution: {recipientName}
              {deliveryMethod ? ` • ${formatLabel(deliveryMethod)}` : ""}
            </Typography>
          )}
          {faxNumber && (
            <Typography variant="body2" className="text-terracotta/80">
              Fax: {formatPhoneNumberToHumanReadable(faxNumber)}
            </Typography>
          )}
          <Typography variant="body2" className="text-terracotta/80">
            Signature Image: {formatYesNo(signatureImageValue)}
          </Typography>
          <Typography variant="body2" className="text-terracotta/60">
            Created: {formatDate(item.createdAt)}
          </Typography>
          <Typography variant="body2" className="text-terracotta/80">
            PDF:{" "}
            {item.pdfUrl ? (
              <a
                href={item.pdfUrl}
                className="underline text-terracotta-600 hover:text-terracotta-700"
                target="_blank"
                rel="noreferrer"
              >
                View document
              </a>
            ) : (
              "Not available"
            )}
          </Typography>
        </div>

        <div className="flex items-center gap-3">
          <SubmitButton
            type="button"
            color="beige"
            hideArrow
            onClick={() =>
              navigate(detailPath ?? `/medical-records/requests/${item.id}`)
            }
          >
            View Details
          </SubmitButton>
          <SubmitButton
            type="button"
            hideArrow
            onClick={handleMarkReady}
            isDisabled={isDisabled}
            isLoading={isPendingForItem}
          >
            Mark Ready To Send
          </SubmitButton>
        </div>
      </div>
    </li>
  );
}

export default MedicalRecordRequestListItem;
